import React, { type FC, useState, useEffect } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import Dialog from '@mui/material/Dialog';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { Flex } from 'src/components/flex';

import { ProfileImageRounded } from 'src/components/ProfileImageRounded';
import DialogContent from '@mui/material/DialogContent';
import { Auth } from 'src/services/api';
import { useSwitchTeam } from 'src/services/api/auth/use-switch-orgs';
import { useOrgTeams } from 'src/services/api/auth/use-org-teams';
import { redirectToNextPath, type TempLoginData } from 'src/pages/landlord/login/LandlordLogin';

import styles from 'src/components/layout/landlord/LandlordTeamSelect.module.css';

type Props = {
    loginData?: TempLoginData;
    onClose?: () => void;
}

/**
 * This component can either be used as part of team selection during login (as a drop-in component)
 * or as a dialog invoked from within the landlord app to switch teams (e.g. as a modal).
 *
 * For the modal version, see the component below: LandlordTeamSelectDialog
 */
export const LandlordTeamSelectPaper: FC<Props> = ({ loginData, onClose }) => {
    const [selectedTeamId, setSelectedTeamId] = useState<number | null>(Auth.loggedInUser?.leasing_team_id || null);
    const tokens = Auth.loggedInUser ? {
        access_token: Auth.accessToken!,
        refresh_token: Auth.refreshToken!,
        user: Auth.loggedInUser,
    } : undefined;
    const currentUser = Auth.loggedInUser;
    const { mutateAsync: loginSwitchingTeam } = useSwitchTeam(tokens || loginData);

    // const { data: loggedInUser } = useLoggedInUser(!loginData?.access_token, loginData?.access_token);

    // NOTE: this hook requires auth tokens to be set in storage
    const { data: orgTeamsData, isLoading } = useOrgTeams();

    useEffect(() => {
        if (!selectedTeamId && orgTeamsData?.teams && orgTeamsData.teams.length > 0) {
            setSelectedTeamId(orgTeamsData.teams[0].id);
        }
    }, [orgTeamsData, selectedTeamId]);

    const availableTeams = orgTeamsData?.teams || [];

    const handleSwitchTeam = async () => {
        if (!selectedTeamId) {
            // no team selected - this should never happen
            return;
        }
        if (currentUser && selectedTeamId === currentUser?.leasing_team_id) {
            if (onClose) {
                onClose();
            } else {
                // current team selected, nothing to do
                redirectToNextPath(currentUser);
            }
            return;
        }

        try {
            const res = await loginSwitchingTeam(selectedTeamId);
            redirectToNextPath(res.user);
        } catch (error) {
            // error handling is done in the hook
            console.debug('Team switch failed', error);
        }
    };

    return (
        <div>
            <Typography variant="h3" fontWeight={500}>Select Team</Typography>
            <div className={styles.teamsList}>
                {availableTeams.map((team) => {
                    return (
                        <Flex key={team.id} justifyContent="space-between" alignItems="center">
                            <Flex alignItems="center" gap={2}>
                                <ProfileImageRounded userName={team.name} width={32} height={32} variant="square" />
                                {team.name}
                            </Flex>
                            <Radio
                                checked={selectedTeamId === team.id}
                                onChange={() => setSelectedTeamId(team.id)}
                                value={team.id}
                            />
                        </Flex>
                    );
                })}
            </div>


            <LoadingButton type="submit" variant="contained" loading={isLoading} fullWidth size="large" onClick={handleSwitchTeam} sx={{ cursor: isLoading ? 'default' : 'pointer' }}>
                {onClose && Auth.loggedInUser?.leasing_team_id === selectedTeamId ? 'Cancel' : 'Continue'}
            </LoadingButton>
        </div>
    );
};

type LandlordTeamSelectDialogProps = {
    open: boolean;
    onClose: () => void;
};

export const LandlordTeamSelectDialog: FC<LandlordTeamSelectDialogProps> = ({ open, onClose }) => {
    return (
        <Dialog open={open} onClose={onClose} closeAfterTransition maxWidth="xs" fullWidth>
            <DialogContent>
                <LandlordTeamSelectPaper onClose={onClose} />
            </DialogContent>
        </Dialog>
    );
};